/**
 * UranIA PA — Login actions (US-010).
 *
 * Wrap di `UserManager.signinRedirect` con:
 *   - `kc_idp_hint`: Keycloak preseleziona l'IdP federato (SPID/CIE).
 *   - `acr_values`: chiede esplicitamente il LoA richiesto (L2 per SPID
 *     Funzionario, CIE per CIE, L3 per CNS — FR-F1-1, analisi §18.2).
 *   - `prompt: 'login'`: forza un round-trip federato anche se Keycloak ha
 *     una sessione SSO attiva (utile per il `/login` re-entry).
 *
 * NB: niente decodifica JWT qui — è il `/auth/callback` (vedi
 * `routes/auth/callback.tsx`) che chiama `signinRedirectCallback()` e popola
 * Zustand store via `setSession`.
 */

import type { User } from 'oidc-client-ts';

import { useAuthStore, type UraniaPrincipal } from './auth-store.ts';
import { getUserManager } from './oidc.ts';

export interface StartFederatedLoginOptions {
  /** Alias Keycloak (es. `spid-testenv2-l2`, `cie-staging`). */
  readonly keycloakAlias: string;
  /** `acr_values` da passare al server (es. `L2`, `CIE`, `L3`). */
  readonly acrValues: string;
  /** Dove Keycloak deve rimandare dopo il consenso. Default: AC US-010 `/dashboard`. */
  readonly returnTo?: string;
}

/**
 * Avvia il flusso OIDC code+PKCE preselezionando l'IdP federato.
 *
 * NB: `signinRedirect` ritorna una Promise che si risolve solo se NON c'è
 * redirect (errore). In condizioni nominali la pagina lascia il tab.
 */
export async function startFederatedLogin(options: StartFederatedLoginOptions): Promise<void> {
  const manager = getUserManager();
  // Dev override: in ambiente locale (`urania-pa-dev` realm) gli IdP federati
  // SPID/CIE/CNS non sono configurati. Saltiamo `kc_idp_hint` cosi' Keycloak
  // mostra la pagina di login standard username/password con gli utenti mock
  // del realm (admin@dev, funz@dev, ri@dev, auditor@dev).
  const isDevRealm =
    typeof import.meta !== 'undefined' &&
    typeof (import.meta as { env?: { VITE_OIDC_AUTHORITY?: string } }).env?.VITE_OIDC_AUTHORITY ===
      'string' &&
    (import.meta as { env: { VITE_OIDC_AUTHORITY: string } }).env.VITE_OIDC_AUTHORITY.includes(
      'urania-pa-dev',
    );

  const extraQueryParams: Record<string, string> = isDevRealm
    ? // Dev: niente acr_values (il realm dev non ha ACR-to-LoA mapping) ne'
      // kc_idp_hint (SPID/CIE/CNS IdP non configurati). Si mostra la form
      // standard username+password con gli utenti mock del realm.
      { prompt: 'login' }
    : {
        kc_idp_hint: options.keycloakAlias,
        acr_values: options.acrValues,
        prompt: 'login',
      };

  await manager.signinRedirect({
    extraQueryParams,
    state: { returnTo: options.returnTo ?? '/dashboard' },
  });
}

/**
 * Chiude il round-trip OIDC: chiamata dal route `/auth/callback`.
 *
 * Effetti:
 *   1. `signinRedirectCallback()` valida `state` + `nonce` PKCE; ritorna
 *      l'oggetto `User` (id_token + access_token).
 *   2. Estrae i claim minimi → costruisce `UraniaPrincipal`.
 *   3. Popola `useAuthStore.setSession` (access_token + principal).
 *   4. Ritorna l'URL di destinazione (default `/dashboard`).
 *
 * Throws se la validazione fallisce → il route renderizza un messaggio
 * d'errore italiano e propone "Riprova".
 */
export async function completeFederatedLogin(): Promise<string> {
  const manager = getUserManager();
  const user: User = await manager.signinRedirectCallback();
  const principal = principalFromUser(user);
  useAuthStore.getState().setSession({ accessToken: user.access_token, principal });
  return resolveReturnTo(user.state);
}

/**
 * Chiude il round-trip OIDC silent renew: chiamata dal route
 * `/auth/silent-renew`.
 */
export async function completeSilentRenew(): Promise<void> {
  const manager = getUserManager();
  await manager.signinSilentCallback();
}

/**
 * Logout federato.
 *
 * `signoutRedirect` invia il browser al `end_session_endpoint` di Keycloak;
 * Keycloak chiude la SSO session e ridireziona a
 * `post_logout_redirect_uri`. Prima del redirect, svuotiamo il Zustand store.
 */
export async function startLogout(): Promise<void> {
  useAuthStore.getState().clearSession();
  const manager = getUserManager();
  await manager.signoutRedirect();
}

/**
 * Costruisce `UraniaPrincipal` dai claim del `User` OIDC.
 *
 * NB difensivo: in Fase 1 non verifichiamo la firma del JWT lato SPA — la
 * verifica avviene all'identity-proxy (US-031). I claim sono prelevati da
 * `user.profile` (id_token decoded da oidc-client-ts).
 */
function principalFromUser(user: User): UraniaPrincipal {
  const profile = user.profile;
  const subject = typeof profile.sub === 'string' ? profile.sub : '';
  const tenantId = readStringClaim(profile, 'tenant_id');
  const acr = readStringClaim(profile, 'acr');
  const preferredUsername = readStringClaim(profile, 'preferred_username');
  const roles = readRealmRoles(profile);
  return Object.freeze({
    subject,
    tenantId,
    acr,
    preferredUsername,
    roles: Object.freeze(roles),
  });
}

type ClaimRecord = Record<string, unknown>;

function readStringClaim(profile: ClaimRecord, key: string): string | null {
  const value = profile[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readRealmRoles(profile: ClaimRecord): readonly string[] {
  const realmAccess = profile.realm_access;
  if (realmAccess === null || typeof realmAccess !== 'object') {
    return [];
  }
  const roles = (realmAccess as ClaimRecord).roles;
  if (!Array.isArray(roles)) {
    return [];
  }
  return roles.filter((r): r is string => typeof r === 'string');
}

function resolveReturnTo(state: unknown): string {
  if (state !== null && typeof state === 'object' && 'returnTo' in state) {
    const returnTo = (state as { returnTo?: unknown }).returnTo;
    if (typeof returnTo === 'string' && returnTo.startsWith('/')) {
      return returnTo;
    }
  }
  return '/dashboard';
}
